import { shiftToToday } from "./beanAging";
import { effectiveConfig, type PredictorConfig } from "./config";
import { compareDateTime, dateOf, daysBetween, type IsoDate } from "./dates";
import { formatDial, formatSeconds } from "./format";
import { clampToDial, midpoint, roundToDial, type Bean, type Grinder, type Prediction, type Shot } from "./model";

/**
 * Grind-setting predictor. Line-for-line port of GrindPredictor.java; see that file (and the Stage 2 spec) for the
 * numbered steps. Pure TypeScript, no DOM (enforced by tsconfig.engine.json).
 *
 *   ln(t_norm) = ln(a) + b · x        (b < 0: higher setting = coarser = faster)
 *
 * The optional starting-point PseudoObservation joins only the Step 7 centroid.
 */

export type ShotKind = "valid" | "choked" | "channeled";

/** Starting-point prior as a pseudo-observation (x, lnT) with weight w. Centroid only. */
export interface PseudoObservation {
  readonly x: number;
  readonly lnT: number;
  readonly weight: number;
}

/** Result of a fit, with nEff exposed for the dialed-in test. */
export interface Fit {
  readonly prediction: Prediction;
  readonly nEff: number;
}

interface Obs {
  readonly shot: Shot;
  readonly x: number;
  readonly tNorm: number;
  readonly y: number;
  readonly weight: number;
  readonly daysAgo: number;
}

export function classify(tNorm: number, cfg: PredictorConfig): ShotKind {
  if (tNorm > cfg.maxValidTimeS) return "choked";
  if (tNorm < cfg.minValidTimeS) return "channeled";
  return "valid";
}

export function recencyWeight(daysAgo: number, cfg: PredictorConfig): number {
  return Math.pow(0.5, daysAgo / cfg.recencyHalfLifeD);
}

/** No shots at all and no prior: suggest the dial midpoint. Mirrors Prediction.noData(Grinder). */
/** Writes a grind setting inside explanation strings; the app can pass a grinder-aware one. Default: decimal. */
export type DialFormatter = (x: number, g: Grinder) => string;

export function noData(g: Grinder, fmt: DialFormatter = formatDial): Prediction {
  const mid = midpoint(g);
  const rounded = roundToDial(g, mid);
  return {
    grindSetting: rounded,
    grindSettingRaw: mid,
    uncertainty: NaN,
    slopeUsed: g.priorSlopeB,
    lnIntercept: NaN,
    priorWeight: 1.0,
    validShots: 0,
    censoredShotsUsed: 0,
    atBound: false,
    conflictingBounds: false,
    xPrior: NaN,
    priorSource: "",
    explanation: `No shots yet — pull a first shot at ${fmt(rounded, g)} and log it.`,
  };
}

export function predict(g: Grinder, bean: Bean, history: readonly Shot[], today: IsoDate, cfg: PredictorConfig,
                        prior: PseudoObservation | null = null, priorSource = "", fmt: DialFormatter = formatDial): Prediction {
  return fit(g, bean, history, today, cfg, prior, priorSource, fmt).prediction;
}

/**
 * @param g           grinder the shots were pulled on
 * @param bean        the bean (roast / freeze dates, decaf, per-bean target)
 * @param history     all shots for this bean on this grinder, any order
 * @param today       ISO date to predict for
 * @param baseCfg     tunables; the bean's own target time overrides targetTimeS when set
 * @param prior       starting-point pseudo-observation, or null
 * @param priorSource wording for where the prior came from
 */
export function fit(g: Grinder, bean: Bean, history: readonly Shot[], today: IsoDate, baseCfg: PredictorConfig,
                    prior: PseudoObservation | null = null, priorSource = "", fmt: DialFormatter = formatDial): Fit {
  const cfg = effectiveConfig(baseCfg, bean);
  const valid: Obs[] = [];
  const choked: Obs[] = [];
  const channeled: Obs[] = [];

  for (const s of history) {
    if (g.id !== null && s.grinderId !== g.id) {
      throw new RangeError(`history contains a shot from another grinder: ${JSON.stringify(s)}`);
    }
    const shotDate = dateOf(s.timestamp);
    const tNorm = (s.timeSeconds * cfg.targetYieldG) / s.yieldG;                   // Step 1
    const y = Math.log(tNorm) - shiftToToday(bean, shotDate, today, cfg);          // Steps 2–3
    const daysAgo = Math.max(0, daysBetween(shotDate, today));
    const weight = recencyWeight(daysAgo, cfg);                                     // Step 5
    const o: Obs = { shot: s, x: s.grindSetting, tNorm, y, weight, daysAgo };
    switch (classify(tNorm, cfg)) {                                                 // Step 4
      case "valid": valid.push(o); break;
      case "choked": choked.push(o); break;
      case "channeled": channeled.push(o); break;
    }
  }

  const chokedRecent = choked.filter((o) => o.daysAgo <= cfg.boundLookbackDays);
  const channeledRecent = channeled.filter((o) => o.daysAgo <= cfg.boundLookbackDays);

  const usePrior = prior !== null && prior.weight > 0;
  const xPriorOut = usePrior ? prior.x : NaN;
  const priorOut = usePrior ? priorSource : "";

  if (valid.length === 0 && !usePrior) {
    if (chokedRecent.length === 0 && channeledRecent.length === 0) return { prediction: noData(g, fmt), nEff: 0 };
    return { prediction: fromCensoredOnly(g, chokedRecent, channeledRecent, fmt), nEff: 0 };
  }

  // Step 6 — slope with shrinkage toward the prior (valid shots only).
  const b0 = g.priorSlopeB;
  const st = valid.length === 0 ? null : weightedStats(valid);
  const nEff = st === null ? 0 : st.sumW;
  let bFinal: number;
  let priorWeight: number;
  if (st === null || valid.length < 2 || st.sdX < cfg.minGrindSpread) {
    bFinal = b0;
    priorWeight = 1.0;
  } else {
    let bObs = st.sxy / st.sxx;
    bObs = Math.max(2 * b0, Math.min(0.5 * b0, bObs));
    const w = st.sumW / (st.sumW + cfg.priorPseudoShots);
    bFinal = w * bObs + (1 - w) * b0;
    priorWeight = 1 - w;
  }

  // Step 7 — centroid; the pseudo-observation joins here only.
  let cx: number, cy: number;
  if (usePrior) {
    const sumW = nEff + prior.weight;
    const sx = (st === null ? 0 : st.meanX * st.sumW) + prior.weight * prior.x;
    const sy = (st === null ? 0 : st.meanY * st.sumW) + prior.weight * prior.lnT;
    cx = sx / sumW;
    cy = sy / sumW;
  } else {
    cx = (st as WeightedStats).meanX;
    cy = (st as WeightedStats).meanY;
  }
  const lnA = cy - bFinal * cx;

  // Step 8 — solve, then censored bounds.
  let xTarget = (Math.log(cfg.targetTimeS) - lnA) / bFinal;
  const margin = 2 * g.dialResolution;
  const lower = chokedRecent.length ? Math.max(...chokedRecent.map((o) => o.x + margin)) : NaN;
  const upper = channeledRecent.length ? Math.min(...channeledRecent.map((o) => o.x - margin)) : NaN;
  let conflicting = false;
  if (!Number.isNaN(lower) && !Number.isNaN(upper) && lower > upper) {
    xTarget = (lower + upper) / 2.0;
    conflicting = true;
  } else {
    if (!Number.isNaN(lower)) xTarget = Math.max(xTarget, lower);
    if (!Number.isNaN(upper)) xTarget = Math.min(xTarget, upper);
  }

  // Step 9 — clamp and round.
  const clamped = clampToDial(g, xTarget);
  const atBound = clamped !== xTarget;
  const xDisplay = roundToDial(g, clamped);

  // Step 10 — uncertainty from valid shots only, around their own line.
  const lnAValid = st === null ? NaN : st.meanY - bFinal * st.meanX;
  const s = valid.length >= 3 ? residualSd(valid, lnAValid, bFinal) : cfg.defaultResidualSd;
  const uncertainty = s / Math.abs(bFinal);

  const censoredUsed = chokedRecent.length + channeledRecent.length;
  const explanation = valid.length === 0
    ? explainPriorOnly(g, xDisplay, priorOut, censoredUsed, atBound, conflicting, fmt)
    : explain(valid, censoredUsed, atBound, conflicting, cfg, usePrior ? priorOut : null);

  return {
    prediction: {
      grindSetting: xDisplay, grindSettingRaw: clamped, uncertainty, slopeUsed: bFinal, lnIntercept: lnA, priorWeight,
      validShots: valid.length, censoredShotsUsed: censoredUsed, atBound, conflictingBounds: conflicting,
      xPrior: xPriorOut, priorSource: priorOut, explanation,
    },
    nEff,
  };
}

// ---------------------------------------------------------------------------------------------------------

function newest(obs: readonly Obs[]): Obs | undefined {
  let best: Obs | undefined;
  for (const o of obs) {
    if (best === undefined || compareDateTime(o.shot.timestamp, best.shot.timestamp) > 0) best = o;
  }
  return best;
}

function fromCensoredOnly(g: Grinder, chokedRecent: readonly Obs[], channeledRecent: readonly Obs[], fmt: DialFormatter): Prediction {
  const step = 2 * (2 * g.dialResolution);
  const lastChoked = newest(chokedRecent);
  const lastChannelled = newest(channeledRecent);

  let xTarget: number;
  let conflicting = false;
  let why = "No valid shots yet; ";
  if (lastChoked !== undefined && lastChannelled !== undefined) {
    const lower = lastChoked.x + step;
    const upper = lastChannelled.x - step;
    xTarget = (lower + upper) / 2.0;
    if (lower > upper) {
      conflicting = true;
      why += `shots choked at ${fmt(lastChoked.x, g)} and channeled at ${fmt(lastChannelled.x, g)} conflict`;
    } else {
      why += `between choked at ${fmt(lastChoked.x, g)} and channeled at ${fmt(lastChannelled.x, g)}`;
    }
  } else if (lastChoked !== undefined) {
    xTarget = lastChoked.x + step;
    why += `last shot choked at ${fmt(lastChoked.x, g)}`;
  } else {
    const lc = lastChannelled as Obs;
    xTarget = lc.x - step;
    why += `last shot channeled at ${fmt(lc.x, g)}`;
  }

  const clamped = clampToDial(g, xTarget);
  const atBound = clamped !== xTarget;
  const xDisplay = roundToDial(g, clamped);
  why += ` — try ${fmt(xDisplay, g)}`;
  if (atBound) why += " (dial limit)";
  why += ".";

  return {
    grindSetting: xDisplay, grindSettingRaw: clamped, uncertainty: NaN, slopeUsed: g.priorSlopeB, lnIntercept: NaN,
    priorWeight: 1.0, validShots: 0, censoredShotsUsed: chokedRecent.length + channeledRecent.length,
    atBound, conflictingBounds: conflicting, xPrior: NaN, priorSource: "", explanation: why,
  };
}

function explainPriorOnly(g: Grinder, xDisplay: number, source: string, censoredUsed: number,
                          atBound: boolean, conflicting: boolean, fmt: DialFormatter): string {
  let sb = `Start at ${fmt(xDisplay, g)}`;
  if (source !== "") sb += ` — ${source}`;
  if (censoredUsed > 0) sb += `; bounded by ${censoredUsed}${censoredUsed === 1 ? " censored shot" : " censored shots"}`;
  if (conflicting) sb += "; choked/channeled bounds conflict";
  if (atBound) sb += "; at dial limit";
  return sb + ".";
}

/** Only what is new; the shot count and target live in the UI subtitle. Mirrors Java. */
function explain(valid: readonly Obs[], censoredUsed: number, atBound: boolean, conflicting: boolean,
                 cfg: PredictorConfig, priorSource: string | null): string {
  const last = newest(valid) as Obs;
  const delta = last.tNorm - cfg.targetTimeS;
  let sb = `Last shot ran ${formatSeconds(Math.abs(delta))} s ${delta >= 0 ? "slow" : "fast"}`;
  if (priorSource !== null && priorSource !== "") sb += `; starting point ${priorSource}`;
  if (censoredUsed > 0) sb += `; bounded by ${censoredUsed}${censoredUsed === 1 ? " censored shot" : " censored shots"}`;
  if (conflicting) sb += "; choked/channeled bounds conflict";
  if (atBound) sb += "; at dial limit";
  return sb + ".";
}

interface WeightedStats {
  readonly sumW: number;
  readonly meanX: number;
  readonly meanY: number;
  readonly sxx: number;
  readonly sxy: number;
  readonly sdX: number;
}

function weightedStats(obs: readonly Obs[]): WeightedStats {
  let sumW = 0, sx = 0, sy = 0;
  for (const o of obs) { sumW += o.weight; sx += o.weight * o.x; sy += o.weight * o.y; }
  const mx = sx / sumW, my = sy / sumW;
  let sxx = 0, sxy = 0;
  for (const o of obs) {
    const dx = o.x - mx;
    sxx += o.weight * dx * dx;
    sxy += o.weight * dx * (o.y - my);
  }
  return { sumW, meanX: mx, meanY: my, sxx, sxy, sdX: Math.sqrt(sxx / sumW) };
}

export function residualSd(obs: readonly Obs[], lnA: number, b: number): number {
  let sumW = 0, ss = 0;
  for (const o of obs) {
    const r = o.y - (lnA + b * o.x);
    sumW += o.weight;
    ss += o.weight * r * r;
  }
  return Math.sqrt(ss / sumW);
}
